/**
 * @license
 * @preserve
 *
 * KeeeX SAS Confidential
 * https://keeex.me
 * Copyright 2013-2022 KeeeX All Rights Reserved.
 * 
 * These computer program listings and specifications, herein,
 * are and remain the property of KeeeX SAS. The intellectual
 * and technical concepts herein are proprietary to KeeeX SAS
 * and may be covered by EU and foreign patents,
 * patents in process, trade secrets and copyright law.
 * 
 * They shall not be reproduced or copied or used in whole or
 * in part as the basis for manufacture or sale of items unless
 * prior written permission is obtained from KeeeX SAS.
 * 
 * For a license agreement, please contact:
 * <mailto: contact@keeex.net>
 * 
 */
import { utf82buf8 } from "@keeex/js-utils/lib/uint8array.js";
import { getProvider, getProviderOptions } from "./provider.js";
import { notImplemented } from "./exception.js";
import ChunkDigest from "./digest/chunkdigest.js";
import { Algorithms, } from "./digesttypes.js";
import { getPureDigest } from "./pureprovider.js";
export { Algorithms, };
/**
 * Return the internal block size for a given digest algorithm, in bytes
 */
export const digestBlockSize = (algorithm) => {
    switch (algorithm) {
        /* eslint-disable @typescript-eslint/no-magic-numbers */
        case Algorithms.SHA1: return 512 / 8;
        case Algorithms.SHA224: return 512 / 8;
        case Algorithms.SHA256: return 512 / 8;
        case Algorithms.SHA512: return 1024 / 8;
        case Algorithms.RIPEMD160: return 512 / 8;
        case Algorithms.SHA3_224: return 1152 / 8;
        case Algorithms.SHA3_256: return 1088 / 8;
        case Algorithms.SHA3_384: return 832 / 8;
        case Algorithms.SHA3_512: return 576 / 8;
        case Algorithms.KECCAK224: return 1152 / 8;
        case Algorithms.KECCAK256: return 1088 / 8;
        case Algorithms.KECCAK384: return 832 / 8;
        case Algorithms.KECCAK512: return 576 / 8;
        /* eslint-enable @typescript-eslint/no-magic-numbers */
    }
};
/**
 * Return the output size for a given digest algorithm, in bytes
 */
export const digestOutputSize = (algorithm) => {
    switch (algorithm) {
        /* eslint-disable @typescript-eslint/no-magic-numbers */
        case Algorithms.SHA1: return 160 / 8;
        case Algorithms.SHA224: return 224 / 8;
        case Algorithms.SHA256: return 256 / 8;
        case Algorithms.SHA512: return 512 / 8;
        case Algorithms.RIPEMD160: return 160 / 8;
        case Algorithms.SHA3_224: return 224 / 8;
        case Algorithms.SHA3_256: return 256 / 8;
        case Algorithms.SHA3_384: return 384 / 8;
        case Algorithms.SHA3_512: return 512 / 8;
        case Algorithms.KECCAK224: return 224 / 8;
        case Algorithms.KECCAK256: return 256 / 8;
        case Algorithms.KECCAK384: return 384 / 8;
        case Algorithms.KECCAK512: return 512 / 8;
        /* eslint-enable @typescript-eslint/no-magic-numbers */
    }
};
/**
 * Use `digest()` to implement `digestImmediate()`
 */
const fallbackDigestImmediate = async (algorithm, data) => {
    const digestProvider = getProvider().digestProvider;
    if (!digestProvider.digest) {
        throw notImplemented();
    }
    const digest = await digestProvider.digest(algorithm);
    return digest.digest(data);
};
/**
 * Compute the digest of a full buffer
 */
export const digestImmediate = (algorithm, data) => {
    const digestProvider = getProvider().digestProvider;
    if (digestProvider.digestImmediate) {
        return digestProvider.digestImmediate(algorithm, data);
    }
    return fallbackDigestImmediate(algorithm, data);
};
/**
 * Compute the digest of a string
 */
export const digestString = (algorithm, data) => digestImmediate(algorithm, utf82buf8(data));
const fallbackDigest = async (algorithm) => {
    const options = getProviderOptions();
    if (options.noImmediateFallback) {
        const pureDigest = (await getPureDigest()).digest;
        if (!pureDigest)
            throw new Error("Unexpected state");
        return pureDigest(algorithm);
    }
    return new ChunkDigest(algorithm, digestImmediate);
};
/**
 * Return a chunk-based digest object
 */
export const digest = (algorithm) => {
    const digestProvider = getProvider().digestProvider;
    if (digestProvider.digest) {
        return digestProvider.digest(algorithm);
    }
    return fallbackDigest(algorithm);
};
